import { useContext, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { PageContext } from "../contexts/PageContext";
import { Org } from "../models/Org";
import { GroupAdd } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { loadRecord, loadRecords } from "../api/connection";
import { UserRole } from "../models/User";
import Minibar from "../components/Minibar";
import PageHeader from "../components/PageHeader";

export default function Organizations() {
  const { isLoggedIn, user } = useContext(PageContext);
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Org[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Org | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });
  const [filter, setFilter] = useState<string>("");
  const [filteredRows, setFilteredRows] = useState<any[]>([]);

  const fetchOrganizations = async (role: UserRole) => {
    try {
      let res: Org[] = [];
      if (role === UserRole.SystemAdmin) {
        res = await loadRecords<Org>("organizations");
      } else if (role === UserRole.OrgAdmin) {
        const org = await loadRecord<Org>("organizations", user!.orgId);
        if (org) {
          res = [org];
        }
      }
      const sortedOrgs = res.sort((a, b) => a.name.localeCompare(b.name));
      setOrganizations(sortedOrgs);
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !isLoggedIn ||
      (user && user?.role !== (UserRole.SystemAdmin || UserRole.OrgAdmin))
    ) {
      navigate("/maintenance/404");
    } else if (
      isLoggedIn &&
      user &&
      user?.role === (UserRole.SystemAdmin || UserRole.OrgAdmin)
    ) {
      fetchOrganizations(user.role);
    }
  }, [isLoggedIn, user]);

  const handleSort = (key: keyof Org) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedOrganizations = [...organizations].sort((a, b) => {
      const aValue = a[key] ?? "";
      const bValue = b[key] ?? "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setOrganizations(sortedOrganizations);
  };

  useEffect(() => {
    const filtered = organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(filter.toLowerCase()) ||
        (org.description &&
          org.description.toLowerCase().includes(filter.toLowerCase()))
    );
    setFilteredRows(filtered);
  }, [filter, organizations]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "#212121",
          color: "#ECECEC",
          minHeight: "100vh",
        }}
      >
        <Backdrop
          open={loading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <PageHeader
          title={"Organizations"}
          rightComponent={[
            <Minibar
              minibarItems={[
                {
                  text: "Add Organization",
                  icon: <GroupAdd />,
                  nav: `/edit-org/new`,
                },
              ]}
              barLength={1}
              searchLabel="Search Organizations"
              onSearch={(e) => setFilter(e)}
            />,
          ]}
        />
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left" width="30%">
                  <TableSortLabel
                    active={sortConfig.key === "name"}
                    direction={
                      sortConfig.key === "name" ? sortConfig.direction : "asc"
                    }
                    onClick={() => handleSort("name")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" width="30%">
                  Description
                </TableCell>

                <TableCell align="left" width="20%">
                  Users
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((organization) => (
                <TableRow hover key={organization._id}>
                  <TableCell align="left">
                    <Link
                      onClick={() =>
                        navigate(`/edit-org/${organization._id}`)
                      }
                      style={{ color: "#b0d1ff", cursor: "pointer" }}
                    >
                      {organization.name}
                    </Link>
                  </TableCell>
                  <TableCell align="left">
                    {organization.description}
                  </TableCell>

                  <TableCell align="left">
                    <Link
                      onClick={() => navigate(`/users/${organization._id}`)}
                      style={{ color: "#b0d1ff", cursor: "pointer" }}
                    >
                      View Users
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
